import { useEffect, useMemo, useState } from "react"

import { formatNumber } from "accounting"
import { Flex, Spin } from "antd"
import { ArcElement, Chart as ChartJS, Tooltip as ChartTooltip, Legend } from "chart.js"
import { Doughnut } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import { INCOGNITO_MASK } from "../../../../Components/Private"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { formatCurrency } from "../../../../Utils/utils"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import { reasonLegendPlugin } from "../reasonLegendPlugin"
import Section from "../Section"
import Toggle from "../Toggle"

import styles from "./distributionDonut.module.less"

ChartJS.register(ArcElement, ChartTooltip, Legend)

const LEGEND_ID = "legend-container-disputes-by-reason"

// On-palette segment colours: bluePrimary, purplePrimary, greenPrimary,
// orangePrimary, redPrimary, blueDepressed, then Neutral400 for "Other".
const PALETTE = ["#225AEA", "#661AC6", "#16895A", "#DF7009", "#D71313", "#1448CB", "#A1A1AA"]

// Prevent's "Alerts by Source" donut tooltip: the dark #27272A HTML box. Lines come
// from tooltip.callbacks.title (0 = heading, 1 = count + value with the gray-circle
// separator). Same colours as Prevent (kept verbatim for parity; a canvas/HTML
// tooltip can't read Less tokens). Uses a dedicated tooltip element id (like the
// country/reason charts) so its wider 236px box can't leave stale width on the
// trend chart's tooltip; still gets the shared #chartjs-tooltip* CSS + cleanup.
const TOOLTIP_ID = "chartjs-tooltip_recover_donut"
const externalDonutTooltip = (context) => {
  let tooltipEl = document.getElementById(TOOLTIP_ID)
  if (!tooltipEl) {
    tooltipEl = document.createElement("div")
    tooltipEl.id = TOOLTIP_ID
    tooltipEl.innerHTML = '<table style="width: 100%;"></table>'
    document.body.appendChild(tooltipEl)
  }
  const tooltipModel = context.tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }
  tooltipEl.classList.remove("above", "below", "no-transform")
  tooltipEl.classList.add(tooltipModel.yAlign || "no-transform")
  tooltipEl.style.zIndex = "1010"
  tooltipEl.style.backgroundColor = "#27272A"
  tooltipEl.style.borderRadius = "12px"
  tooltipEl.style.padding = "12px 12px"
  tooltipEl.style.color = "white"
  tooltipEl.style.width = "236px"
  if (tooltipModel.body) {
    const titleLines = tooltipModel.title || []
    let innerHtml = "<thead>"
    const styleTitle = "padding-bottom: 0px; text-align: start; font-weight: 500; color: #7A7A83"
    const styleTitle2 = "padding: 0px; padding-top: 12px; font-weight: 400; text-align: start"
    titleLines.forEach((value, i) => {
      if (i === 0) innerHtml += '<tr><th style="' + styleTitle + '">' + value + "</th></tr>"
      else if (value) innerHtml += '<tr><th style="' + styleTitle2 + '">' + value + "</th></tr>"
    })
    innerHtml += "</thead><tbody>"
    tooltipEl.querySelector("table").innerHTML = innerHtml
  }
  const position = context.chart.canvas.getBoundingClientRect()
  // Glide between slices while already visible (fade in place on first show, see chartsOptions).
  const wasVisible = parseFloat(tooltipEl.style.opacity || 0) > 0
  tooltipEl.style.transition = wasVisible
    ? "left 0.13s cubic-bezier(0.22, 1, 0.36, 1), top 0.13s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.12s ease"
    : "opacity 0.12s ease"
  tooltipEl.style.opacity = 1
  tooltipEl.style.position = "absolute"
  tooltipEl.style.left = position.left - 77 + window.scrollX + tooltipModel.caretX + "px"
  tooltipEl.style.top = position.top + 20 + window.scrollY + tooltipModel.caretY + "px"
  tooltipEl.style.padding = tooltipModel.padding + "px "
  tooltipEl.style.pointerEvents = "none"
}

// "Disputes by category" = a copy of the Alerts by Source card: the same donut
// (74% cutout, rounded offset segments, centred total) + the same legend table
// (rendered by reasonLegendPlugin into #legend-container-disputes-by-reason).
const DistributionDonut = ({ getData, withBreakdownLink }) => {
  const { t } = useTranslation()
  const theme = useSelector((s) => s.nav.theme)
  // Incognito masks money everywhere else on this page; mirror it in the donut centre,
  // tooltip and legend so the $ volume can't leak while the rest of the page is masked.
  const incognito = useSelector((s) => s.nav.incognito)
  const byCategory = useSelector(selectSectionData(ANALYTICS_SECTION.BY_CATEGORY))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.BY_CATEGORY))
  const navigate = useNavigate()
  const [metric, setMetric] = useState("count")
  // Slices the user has clicked off in the legend (label -> hidden). Reset when
  // the count/volume toggle flips, since that remounts the chart (same as Alerts).
  const [filterData, setFilterData] = useState({})
  useEffect(() => setFilterData({}), [metric])

  useEffect(() => {
    getData(ANALYTICS_SECTION.BY_CATEGORY)
  }, [getData])

  // The tooltip element lives on <body>, outside React, so the chart that creates it removes it.
  useEffect(() => () => document.getElementById(TOOLTIP_ID)?.remove(), [])

  // The 4 network categories (Fraud / Authorization / Processing error / Consumer
  // dispute) plus Other: those are what the networks and the disputes list's Category
  // filter speak in. Biggest first by the chosen metric, Other always last whatever its size.
  // by-category/ carries both metrics, so the toggle never asks again.
  const rows = useMemo(() => {
    const sortKey = metric === "value" ? "value" : "count"
    return (byCategory?.result ?? [])
      .map((row) => ({
        key: row.category,
        label: t(row.label),
        count: row.count,
        value: row.amount
      }))
      .sort((a, b) => (a.key === "unknown") - (b.key === "unknown") || b[sortKey] - a[sortKey])
  }, [byCategory, metric, t])
  const valueOf = (r) => (metric === "value" ? r.value : r.count)
  // Centre total excludes legend-hidden slices, matching Alerts by Source.
  const grandTotal = rows.reduce((acc, r) => acc + (filterData[r.label] ? 0 : valueOf(r)), 0)
  const fmtMetric = (v) =>
    metric === "value" ? formatCurrency(v, true) : formatNumber(v, 0, ",", ".")

  const textCenter = {
    id: "recoverDonutCenter",
    afterDatasetsDraw(chart) {
      const { ctx } = chart
      const meta = chart.getDatasetMeta(0)?.data?.[0]
      if (!meta) return
      ctx.save()
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.font = "400 24px/24px Inter"
      ctx.fillStyle = theme === "dark" ? "#FCFCFC" : "#101828"
      ctx.fillText(
        metric === "value" && incognito ? INCOGNITO_MASK : fmtMetric(grandTotal),
        meta.x,
        meta.y - 10
      )
      ctx.font = "400 14px/20px Inter"
      ctx.fillStyle = "#7A7A83"
      ctx.fillText(metric === "value" ? t("Volume") : t("Disputes"), meta.x, meta.y + 12)
      ctx.restore()
    }
  }

  const data = {
    labels: rows.map((r) => r.label),
    datasets: [
      {
        data: rows.map(valueOf),
        backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]),
        borderColor: theme === "dark" ? "#141414" : "#FFFFFF",
        borderRadius: 6,
        hoverOffset: 10,
        hoverBorderColor: "transparent",
        offset: 4,
        cutout: "74%",
        total: grandTotal,
        count: rows.map((r) => r.count),
        volume: rows.map((r) => r.value)
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      htmlLegend: { containerID: LEGEND_ID },
      legend: { display: false },
      tooltip: {
        enabled: false,
        callbacks: {
          // line 0 = reason (muted heading); line 1 = count + value with the
          // gray-circle separator, mirroring Alerts by Source.
          title: (ctx) => {
            const r = rows[ctx?.[0]?.dataIndex]
            if (!r) return ["", ""]
            return [
              r.label,
              formatNumber(r.count, 0, ",") +
                '<span class="gray-circle"></span>' +
                (incognito ? INCOGNITO_MASK : formatCurrency(r.value))
            ]
          }
        },
        external: externalDonutTooltip
      }
    }
  }

  const toggle = (
    <Toggle
      value={metric}
      onChange={setMetric}
      options={[
        { value: "count", label: "By count" },
        { value: "value", label: "By volume" }
      ]}
    />
  )

  // In the donut-only layout this card is the way into the reason-code breakdown, so it
  // takes the "View all" link the trends card would otherwise carry.
  const extra = withBreakdownLink ? (
    <Flex align="center" gap={12} wrap="wrap">
      {toggle}
      <Button
        type="link"
        onClick={() => navigate(RoutesPaths.recover.analytics_reason_codes)}
        icon={<Icon type="chevron_right_small" />}
        iconPosition="end"
        style={{ padding: 0 }}
        size="small"
        text={t("View All")}
      />
    </Flex>
  ) : (
    toggle
  )

  return (
    <Section title={t("Disputes by category")} extra={extra} minHeight={536}>
      <Spin spinning={fetching}>
        <div className={styles.donut}>
          <Doughnut
            key={`${metric}-${theme}-${incognito}`}
            data={data}
            options={options}
            plugins={[reasonLegendPlugin(setFilterData, t, incognito), textCenter]}
          />
        </div>
        <div id={LEGEND_ID} className={styles.legend} />
      </Spin>
    </Section>
  )
}

export default DistributionDonut
